import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { AlertFactory } from '@/features/shell'
import { type Bundle } from '@/features/admin/bundle/Bundle'
import { type BundleStore } from '@/features/admin/bundle/BundleStore'
import { type RefreshBundlesFn } from '@/features/admin/bundle/BundleSearchViewModel'
import {
  formatBundleNames,
  pluralizeBundles,
  showBulkActionError,
  showBulkActionResult,
} from '@/features/admin/bundle/bulk-action-alerts'
import { type Id } from '@/infra/id'

export class BulkArchiveBundleDialogViewModel {
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  private readonly _prompt = new AsyncPrompt<boolean>({
    abort$: this.modal.close$,
  })

  @observable
  accessor selectedBundles: Bundle[] = []

  constructor(
    private readonly _bundleStore: BundleStore,
    private readonly _newAlert: AlertFactory,
    private readonly _refreshBundles: RefreshBundlesFn,
  ) {}

  /**
   * Already-archived bundles archive to a harmless no-op, so they're filtered
   * out before sending; anything that slips through lands in
   * `alreadyArchivedIds`.
   */
  @computed
  get archivableBundles() {
    return this.selectedBundles.filter((bundle) => !bundle.archived)
  }

  /**
   * Asks the user to archive the bundles.
   *
   * @returns Whether the bundles were archived.
   */
  async prompt(bundles: Bundle[], signal?: AbortSignal) {
    this.modal.close()

    const responsePromise = this._prompt.prompt(signal)
    runInAction(() => {
      this.reset()
      this.selectedBundles = bundles
      this.modal.show()
    })

    const response = await responsePromise
    this.modal.close()

    return response ?? false
  }

  @action.bound
  cancel() {
    this.modal.close()
  }

  @action.bound
  reset() {
    this.selectedBundles = []

    this.archive.reset()
  }

  archive = task.resolved(async () => {
    const bundles = this.archivableBundles

    if (bundles.length === 0) {
      return
    }

    try {
      const result = await this._bundleStore.bulkArchive({
        workspaceId: bundles[0].workspace.id,
        ids: bundles.map((bundle) => bundle.id),
      })

      const skippedIds = new Set<Id>([
        ...result.notFoundIds,
        ...result.alreadyArchivedIds,
      ])

      showBulkActionResult(this._newAlert, bundles, skippedIds, {
        success: (count) => `Archived ${count} ${pluralizeBundles(count)}.`,
        partialTitle: 'Some bundles were skipped',
        partial: (count, skipped) =>
          `Archived ${count} of ${count + skipped.length} bundles. Skipped: ${formatBundleNames(skipped)}.`,
      })
    } catch (e) {
      showBulkActionError(
        this._newAlert,
        e,
        'You do not have permission to archive bundles in this workspace.',
        'There was an error archiving the bundles.',
      )
      this.modal.close()
      return
    }

    await this._refreshBundles(bundles[0].workspace)

    this._prompt.respond(true)
    this.modal.close()
  })
}
